// FTEDN Team — Dashboard helpers (analytics, quick action validation)

export interface DashboardStats {
  totalCreators: number;
  totalContent: number;
  totalCampaigns: number;
  pendingModeration: number;
  activeTrials: number;
  signupsToday: number;
}

/** Map raw analytics response to DashboardStats */
export function mapDashboardStats(raw: Record<string, unknown>): DashboardStats {
  return {
    totalCreators: Number(raw.totalCreators ?? 0),
    totalContent: Number(raw.totalContent ?? 0),
    totalCampaigns: Number(raw.totalCampaigns ?? 0),
    pendingModeration: Number(raw.pendingModeration ?? 0),
    activeTrials: Number(raw.activeTrials ?? 0),
    signupsToday: Number(raw.signupsToday ?? 0),
  };
}

/** Format a large number for display (1,234 → 1.2K, etc.) */
export function formatStatNumber(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1)}K`;
  if (n < 1000000) return `${Math.floor(n / 1000)}K`;
  return `${(n / 1000000).toFixed(1)}M`;
}

/** Calculate growth percentage between two values */
export function calculateGrowth(current: number, previous: number): { percent: number; direction: 'up' | 'down' | 'flat' } {
  if (previous === 0) {
    return current > 0 ? { percent: 100, direction: 'up' } : { percent: 0, direction: 'flat' };
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct > 0) return { percent: pct, direction: 'up' };
  if (pct < 0) return { percent: Math.abs(pct), direction: 'down' };
  return { percent: 0, direction: 'flat' };
}

export type QuickActionType = 'hide' | 'delete' | 'lock' | 'restore' | 'warn' | 'suspend' | 'unsuspend';

/** Validate a quick action before execution */
export function validateQuickAction(
  action: string,
  targetId: string,
  reason?: string,
): { valid: true } | { valid: false; error: string } {
  const validActions: QuickActionType[] = ['hide', 'delete', 'lock', 'restore', 'warn', 'suspend', 'unsuspend'];
  if (!validActions.includes(action as QuickActionType)) {
    return { valid: false, error: `Invalid action: ${action}` };
  }
  if (!targetId || targetId.trim().length === 0) {
    return { valid: false, error: 'Target ID is required' };
  }
  if (!reason || reason.trim().length < 5) {
    return { valid: false, error: 'A reason (5+ chars) is required for moderation actions' };
  }
  return { valid: true };
}

export interface ModerationQueueItem {
  id: string;
  source: 'report' | 'auto-flag';
  contentRef: string;
  targetUserId: string;
  reason: string;
  score: number;
  status: string;
  createdAt: string;
}

/** Map raw queue items */
export function mapQueueItem(raw: Record<string, unknown>): ModerationQueueItem {
  return {
    id: String(raw.id ?? ''),
    source: (raw.source as 'report' | 'auto-flag') ?? 'report',
    contentRef: String(raw.contentRef ?? ''),
    targetUserId: String(raw.targetUserId ?? ''),
    reason: String(raw.reason ?? ''),
    score: Number(raw.score ?? 0),
    status: String(raw.status ?? 'pending'),
    createdAt: String(raw.createdAt ?? ''),
  };
}

/** Sort queue items by severity (higher score first) */
export function sortQueueBySeverity(items: ModerationQueueItem[]): ModerationQueueItem[] {
  return [...items].sort((a, b) => b.score - a.score);
}

/** Get urgency label based on score */
export function getUrgencyLabel(score: number): { label: string; level: 'low' | 'medium' | 'high' | 'critical' } {
  if (score >= 0.9) return { label: '🔴 Critical', level: 'critical' };
  if (score >= 0.7) return { label: '🟠 High', level: 'high' };
  if (score >= 0.4) return { label: '🟡 Medium', level: 'medium' };
  return { label: '🟢 Low', level: 'low' };
}
